/** A PaymentIntent as Stripe.js reports it: on success, and on the error of a confirm it refused. */
interface StripeIntent {
    id: string;
    status: string;
}

/** What Stripe.js's `confirmCardPayment` resolves to. */
export interface CardConfirmation {
    error?: { message?: string; payment_intent?: StripeIntent | null } | null;
    paymentIntent?: StripeIntent | null;
}

export interface StripeLike {
    confirmCardPayment: (clientSecret: string) => Promise<CardConfirmation>;
    retrievePaymentIntent: (clientSecret: string) => Promise<{ paymentIntent?: StripeIntent | null }>;
}

/** The statuses of an intent whose money has moved or is moving: the bank step took the payment. */
const CHARGING_STATUSES: ReadonlySet<string> = new Set(['succeeded', 'processing']);

/**
 * The intent a bank step charged, or null when it took nothing. Read from the error too: Stripe.js
 * refuses to confirm an intent that has already succeeded (a second tab, a repeated press) and
 * reports that intent on the error, which is still a charge whatever the error says.
 */
export function chargedIntent(confirmation: CardConfirmation): StripeIntent | null {
    const intent = confirmation.paymentIntent ?? confirmation.error?.payment_intent ?? null;
    return intent && CHARGING_STATUSES.has(intent.status) ? intent : null;
}

/**
 * app-api's answer for a charge whose card the payer's bank must authenticate
 * (`ThreeDSecureV3Resource`): the PaymentIntent exists and waits on the payer, and nothing has been
 * taken yet. A payment route sends it with a 2xx in place of the record it would return once paid.
 */
export interface ThreeDSecureChallenge {
    requires_action: true;
    payment_intent_client_secret: string | null;
    invoice_id: string;
    message: string;
}

/** Whether a payment route's answer is the bank step rather than the paid record. */
export function isThreeDSecureChallenge(payload: unknown): payload is ThreeDSecureChallenge {
    return typeof payload === 'object' && payload !== null && (payload as { requires_action?: unknown }).requires_action === true;
}

/**
 * `confirmPayment` is the server-side finalization, and what it resolves to is what the server
 * said about the settled payment — `void` for a confirm that answers nothing a caller needs, the
 * settled record for one that does — handed back on the success result so the caller never reads
 * it a second time. `signal` ends the wait on a charge still processing, for a page the payer has
 * left.
 */
export interface ThreeDSecureDeps<Confirmed = void> {
    stripeKey: string | undefined;
    loadStripe: (key: string) => Promise<StripeLike | null>;
    confirmPayment: (args: { payment_intent_id: string; invoice_id: string }) => Promise<Confirmed>;
    signal?: AbortSignal;
}

/**
 * How long a charge the bank left `processing` is watched for an outcome. A card charge usually
 * leaves it within seconds; one that has not by then is told it is processing, not sent to
 * support, because nothing has gone wrong and the record follows when Stripe settles it.
 */
const PROCESSING_CHECKS = 5;
const PROCESSING_INTERVAL_MS = 2000;

/** What the payer reads for a charge still processing when the watch ends. */
export const PROCESSING_MESSAGE = "Your payment is processing. We'll confirm it shortly, so there's no need to pay again.";

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * The intent once it has left `processing`, or as it last read when the watch ended. An unreadable
 * check is one more check still processing: nothing is concluded from a read that failed.
 */
async function outcomeOf(stripe: StripeLike, clientSecret: string, intent: StripeIntent, signal?: AbortSignal): Promise<StripeIntent> {
    let latest = intent;
    for (let check = 0; latest.status === 'processing' && check < PROCESSING_CHECKS && !signal?.aborted; check++) {
        await sleep(PROCESSING_INTERVAL_MS);
        if (signal?.aborted) break;
        try {
            const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
            if (paymentIntent) latest = paymentIntent;
        } catch {
            // Still processing, as far as anyone knows.
        }
    }
    return latest;
}

export type ThreeDSecureResult<Confirmed = void> =
    | { ok: true; confirmed: Confirmed }
    | { ok: false; charged: boolean; message: string };

/**
 * Drives the post-purchase SCA (3D Secure) challenge and server-side finalization.
 * Stripe and the confirm call are injected so the decision logic is testable in isolation.
 */
export async function runThreeDSecureFlow<Confirmed = void>(
    payload: { payment_intent_client_secret: string; invoice_id: string },
    deps: ThreeDSecureDeps<Confirmed>,
): Promise<ThreeDSecureResult<Confirmed>> {
    if (!deps.stripeKey) {
        return { ok: false, charged: false, message: 'Payment authentication is unavailable — please contact support.' };
    }
    const stripe = await deps.loadStripe(deps.stripeKey);
    if (!stripe) {
        return { ok: false, charged: false, message: 'Could not initialize payment authentication.' };
    }

    const confirmation = await stripe.confirmCardPayment(payload.payment_intent_client_secret);
    const charged = chargedIntent(confirmation);
    if (!charged) {
        // Card not charged — a normal retry is safe.
        return { ok: false, charged: false, message: confirmation.error?.message ?? 'Card authentication was not completed.' };
    }

    // Money that is moving is neither taken nor refused yet: the server can record only a charge
    // that succeeded, so a processing one is watched until it says which.
    const intent = await outcomeOf(stripe, payload.payment_intent_client_secret, charged, deps.signal);
    if (intent.status === 'processing') {
        // Do not retry: the payment may still complete.
        return { ok: false, charged: true, message: PROCESSING_MESSAGE };
    }
    if (intent.status !== 'succeeded') {
        // The bank turned the processing charge down: nothing was taken, and a retry is safe.
        return { ok: false, charged: false, message: 'Your bank did not complete this payment.' };
    }

    // The card is charged at this point. A failure to finalize server-side must be reported
    // distinctly — never as a generic retryable error — to avoid a duplicate charge.
    let confirmed: Confirmed;
    try {
        confirmed = await deps.confirmPayment({
            payment_intent_id: intent.id,
            invoice_id: payload.invoice_id,
        });
    } catch {
        return {
            ok: false,
            charged: true,
            message: `Your payment was received but we couldn't confirm it. Please contact support with reference ${intent.id} and do not retry — this avoids a duplicate charge.`,
        };
    }
    return { ok: true, confirmed };
}
